import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  AbstractControl,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { v4 as uuidv4 } from "uuid";
import { Subscription } from "rxjs";
import { FreightCarriersGroupDto } from "../../../core/models/freight-quote/freightCarriersGroupDto";
import { FreightShipmentDto } from "../../../core/models/freight-quote/freightShipmentDto";
import { FreightShipmentRequestDto } from "../../../core/models/freight-quote/freightShipmentRequestDto";
import { AddNewClientService } from "../../../core/services/add-new-client.service";
import { FreightQuoteService } from "../../../core/services/freight-quote.service";
import { FreightCarriersListDto } from "../../../core/models/freight-quote/freightCarriersListDto";
import { ResidentialEvent } from "../../../core/models/freight-quote/residentialEvent";
import { AddressCountryRegion } from "../../../core/models/client/addclient/countryRegionList";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { LoaderService } from "../../../core/services/loader.service";
import { CommonModule } from "@angular/common";
import { CheckboxModule } from "primeng/checkbox";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { DatePickerModule } from "primeng/datepicker";
import { ConfirmDialogComponent } from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import { FreightPackagesFormComponent } from "../freight-packages-form/freight-packages-form.component";
import { FreightChargesDetailComponent } from "../freight-charges-detail/freight-charges-detail.component";
import { FreightAddressesFormComponent } from "../freight-addresses-form/freight-addresses-form.component";
import { FreightShipmentSectionDto } from "../../../core/models/freight-quote/freightShipmentSectionDto";
import { FreightShipmentSectionAddressDto } from "../../../core/models/freight-quote/freightShipmentSectionAddressDto";
import { FreightShipmentSectionPackageDto } from "../../../core/models/freight-quote/freightShipmentSectionPackageDto";
import {
  AlertList,
  ShipmentList,
} from "../../../core/models/freight-quote/freightApiResponse.dto";
import { SaleOrderLineAddressDto } from "../../../core/models/orders/saleOrderLineAddressDto";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { CardModule } from "primeng/card";
import { TooltipModule } from "primeng/tooltip";

@UntilDestroy()
@Component({
  selector: "jni-freight-quotation-form",
  templateUrl: "./freight-quotation-form.component.html",
  styleUrls: ["./freight-quotation-form.component.scss"],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CheckboxModule,
    SelectModule,
    DatePickerModule,
    ConfirmDialogComponent,
    FreightPackagesFormComponent,
    FreightChargesDetailComponent,
    FreightAddressesFormComponent,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    CardModule,
    TooltipModule,
  ],
})
export class FreightQuotationFormComponent implements OnInit, OnDestroy {
  //@Input() clientList = [];
  @Input() saleOrderId = "";
  @Input() purchaseSaleOrderId = "";
  @Input() accountNumber = "";
  @Input() partyNumber = "";
  @Input() jobName = "";
  @Input() selectedLineItems: SaleOrderLineAddressDto[] = [];
  @Input() quoteId!: number;
  estimations!: FreightShipmentDto;
  @ViewChild("scrollContainer") scrollContainer!: ElementRef;
  @Output() formChanged = new EventEmitter<boolean>();
  @Output() closeModalEvent = new EventEmitter();

  fromCountry = ""; //toBeChangedIntoArray
  toCountry = ""; //toBeChangedIntoArray
  showSmallLoader!: boolean;
  address = "";
  fromAddress = ""; //toBeChangedIntoArray
  toAddress = ""; //toBeChangedIntoArray
  countryRegionList: AddressCountryRegion[] = [];
  carriers: FreightCarriersGroupDto[] = [];
  selectedCarrier = "";
  selectedCarrierList: FreightCarriersListDto[][] = [];
  viewType = 1;
  totalPackages = 0;
  totalWeight = 0;
  displayPrice = false;
  shippingResponse!: FreightShipmentDto;
  // estimations: any;
  freightFormValues = {} as FreightShipmentDto;
  isFreightFormChanged = false;
  minDate = new Date();
  addressClearMessage = "You want to clear all addresses";
  clearAlertVisible = false;
  selectedAddressClear = "";
  qouteTotalPrice = "";
  freightForm!: FormGroup;
  private formSubscription!: Subscription;
  tooltipHtml = `<div class='custom-tooltip' style="color: yellow;"><strong>Weight Limit Consideration  – </strong>If your total shipment weight exceeds 250 lbs,
  please contact Freight@nadel.com for an air freight quote, as this tool is optimized for standard shipments under this limit.</div>`;

  constructor(
    private fb: FormBuilder,
    private _toastr: JniToastrService,
    private _addNewClientService: AddNewClientService,
    private _freightQuoteService: FreightQuoteService,
    private _loaderService: LoaderService,
    private route: ActivatedRoute
  ) {
    this.route.params.subscribe(params => {
      this.saleOrderId = params["id"];
    });
    this.freightForm = this.fb.group({
      id: new FormControl(0),
      name: new FormControl("", [Validators.required]),
      quoteRefernceId: new FormControl(uuidv4()),
      quoteDescription: new FormControl(""),
      personnalNumber: new FormControl(""),
      salesOrderIdD365: new FormControl(""),
      pONumberD365: new FormControl(""),
      freightEstimationSections: this.fb.array([this.createShipment()]),
    });
  }

  createShipment(): FormGroup {
    const shipmentReferenceId = uuidv4();
    return this.fb.group({
      id: new FormControl(0),
      shipmentReferenceId: new FormControl(shipmentReferenceId),
      shipmentDescription: new FormControl(""),
      poNumber: new FormControl(""),
      shippingCarrier: new FormControl("FEDEX"),
      shipmentMethodCode: new FormControl("SHOP"),
      shipmentMethodDescription: new FormControl("FedEx SHOP"),
      shipDate: new FormControl(new Date(), [Validators.required]),
      pricingCurrencyCode: new FormControl("USD"),
      totalPackages: new FormControl(0),
      totalWeight: new FormControl(0),
      isMetricUnits: new FormControl(false),
      shipmentReferenceUpdated: new FormControl(false),
      addresses: this.fb.array([
        this.createAddress("From"),
        this.createAddress("To"),
      ]),
      freightEstimationSectionPackages: this.fb.array([this.createPackage()]),
    });
  }

  createAddress(type: string): FormGroup {
    return this.fb.group({
      id: new FormControl(0),
      type: new FormControl(type),
      name: new FormControl("", [Validators.required]),
      attention: new FormControl(""),
      address1: new FormControl(""),
      address2: new FormControl(""),
      address3: new FormControl(""),
      city: new FormControl(""),
      melissaKey: new FormControl(""),
      stateProvinceISO2: new FormControl(""),
      country: new FormControl("USA"),
      postalCode: new FormControl(""),
      vendor: new FormControl(false),
      customer: new FormControl(false),
      l: new FormControl(false),
      shipAddressType: new FormControl(""),
      formattedAddress: new FormControl("", [Validators.required]),
      shipAddressAccount: new FormControl(""),
      isResidential: new FormControl(false),
      isAppartmentFieldStatus: new FormControl(false),
      buildingComplimentD365: new FormControl(""),
      freightSectionId: new FormControl(0),
    });
  }

  createPackage(): FormGroup {
    return this.fb.group({
      id: new FormControl(0),
      packageReferenceId: new FormControl(uuidv4()),
      packageApiReferenceId: new FormControl(""),
      packageDescription: new FormControl(""),
      packageCount: new FormControl(null, [Validators.required]),
      packageWeight: new FormControl(null, [Validators.required]),
      weightUnitCode: new FormControl("LB"),
      packageLength: new FormControl(null),
      packageHeight: new FormControl(null),
      dimentsionUnitCode: new FormControl("IN"),
      packageDepth: new FormControl(null),
      totalWeight: new FormControl(0),
      totalPrice: new FormControl(0),
      packageReferenceUpdated: new FormControl(false),
      freightSectionId: new FormControl(0),
    });
  }

  get shipments(): FormArray {
    return this.freightForm.get("freightEstimationSections") as FormArray;
  }

  getShipment(index: number): FormGroup {
    return this.shipments.at(index) as FormGroup;
  }

  getAddressArray(shipment: AbstractControl): FormArray {
    return (shipment.get("addresses") as FormArray) ?? this.fb.array([]);
  }

  getPackageArray(shipment: AbstractControl): FormArray {
    return (
      (shipment.get("freightEstimationSectionPackages") as FormArray) ??
      this.fb.array([])
    );
  }

  appendShipment() {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    shipmentArray.push(this.createShipment());

    this.onChangeCarrier("FEDEX");
  }

  appendNext(shipmentIndex: number) {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const shipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const addresses = shipment.get("addresses") as FormArray;
    addresses.push(this.createAddress("From"));
    addresses.push(this.createAddress("To"));
  }

  setAddressGroupShipmentFrom(shipmentIndex: number) {
    this.appendShipment();

    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const newShipmentIndex = shipmentArray.length - 1;

    const currentShipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const nextShipment = shipmentArray.at(newShipmentIndex) as FormGroup;
    nextShipment.patchValue({
      shipmentReferenceId: currentShipment.value.shipmentReferenceId,
      shipDate: currentShipment.value.shipDate,
      shippingCarrier: currentShipment.value.shippingCarrier,
      shipmentMethodCode: currentShipment.value.shipmentMethodCode,
      shipmentMethodDescription:
        currentShipment.value.shipmentMethodDescription,
      isMetricUnits: currentShipment.value.isMetricUnits,
    });
    const currentAddresses = currentShipment.get("addresses") as FormArray;
    const nextAddresses = nextShipment.get("addresses") as FormArray;
    const fromAddress = currentAddresses.at(
      currentAddresses.value.length - 1
    ) as FormGroup;
    const firstToAddressInNextFrom = nextAddresses.at(0) as FormGroup;
    firstToAddressInNextFrom.patchValue({
      type: "From",
      name: fromAddress.value.name || "",
      formattedAddress: fromAddress.value.formattedAddress || "",
      attention: fromAddress.value.attention || "",
      address1: fromAddress.value.address1 || "",
      address2: fromAddress.value.address2 || "",
      address3: fromAddress.value.address3 || "",
      city: fromAddress.value.city || "",
      melissaKey: fromAddress.value.melissaKey || "",
      stateProvinceISO2: fromAddress.value.stateProvinceISO2 || "",
      country: fromAddress.value.country || "USA",
      vendor: fromAddress.value.vendor || false,
      customer: fromAddress.value.customer || false,
      l: fromAddress.value.l || false,
      isResidential: fromAddress.value.isResidential || false,
      shipAddressType: fromAddress.value.shipAddressType,
      postalCode: fromAddress.value.postalCode,
    });
    firstToAddressInNextFrom.get("name")?.updateValueAndValidity();
    firstToAddressInNextFrom.get("formattedAddress")?.updateValueAndValidity();
    this.onPackageUpdate(currentShipment.value.shipmentReferenceId);
  }

  setAddressGroupShipment(shipmentIndex: number) {
    this.appendShipment();

    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const newShipmentIndex = shipmentArray.length - 1;

    const existingShipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const newShipment = shipmentArray.at(newShipmentIndex) as FormGroup;
    newShipment.patchValue({
      shipmentReferenceId: existingShipment.value.shipmentReferenceId,
      shipDate: existingShipment.value.shipDate,
      shippingCarrier: existingShipment.value.shippingCarrier,
      shipmentMethodCode: existingShipment.value.shipmentMethodCode,
      shipmentMethodDescription:
        existingShipment.value.shipmentMethodDescription,
      isMetricUnits: existingShipment.value.isMetricUnits,
    });
    this.onPackageUpdate(existingShipment.value.shipmentReferenceId);
  }

  onNewShipmentDetail(
    shipmentReferenceId: string,
    field: string,
    event: Event
  ) {
    const target = event.target as HTMLInputElement;
    const referenceShipments =
      this.getAddressesFromShipments(shipmentReferenceId);

    if (referenceShipments.length > 1) {
      const lastShipment = referenceShipments[referenceShipments.length - 1];
      lastShipment.patchValue({
        [field]: target?.value,
      });
    }
  }

  ungroupAddressShipment(shipmentReferenceId: string) {
    const referencedShipments = this.shipments.controls.filter(
      (control: AbstractControl) => {
        const shipment = control as FormGroup;
        return shipment.value.shipmentReferenceId === shipmentReferenceId;
      }
    );

    if (referencedShipments.length > 1) {
      const lastShipment = referencedShipments[referencedShipments.length - 1];
      lastShipment.patchValue({
        shipmentReferenceId: uuidv4(),
      });

      const packages = lastShipment.get(
        "freightEstimationSectionPackages"
      ) as FormArray;
      if (packages?.length > 0) {
        while (packages.length > 1) {
          packages.removeAt(packages.length - 1);
        }
        const pkg = packages.at(0);
        if (pkg instanceof FormGroup) {
          const patchMap: Record<string, string | boolean> = {
            weightUnitCode: "LB",
            dimentsionUnitCode: "IN",
            packageDescription: "",
            packageCount: "",
            packageWeight: "",
            packageLength: "",
            packageDepth: "",
            packageHeight: "",
            packageReferenceUpdated: false,
            packageApiReferenceId: "",
          };

          Object.entries(pkg.controls).forEach(([key, control]) => {
            if (control) {
              if (key === "packageReferenceId") {
                control.patchValue(uuidv4());
              } else if (key in patchMap) {
                control.patchValue(patchMap[key]);
              } else {
                control.patchValue(0);
              }
            }
          });
        }
      }
    }
  }

  ungroupAddressWithCopy(shipmentReferenceId: string) {
    const referencedShipments = this.shipments.controls.filter(
      (control: AbstractControl) => {
        const shipment = control as FormGroup;
        return shipment.value.shipmentReferenceId === shipmentReferenceId;
      }
    );

    if (referencedShipments.length > 1) {
      const firstShipment = referencedShipments[0];
      const firstPackagesArray = this.getPackageArray(firstShipment);

      const shipmentIndex = referencedShipments.length - 1;
      const lastShipment = referencedShipments[shipmentIndex];
      const lastShipmentReferenceId = uuidv4();
      lastShipment.patchValue({
        shipmentReferenceId: lastShipmentReferenceId,
      });

      const lastPackageArray = this.getPackageArray(lastShipment);
      if (firstPackagesArray?.length > 0) {
        firstPackagesArray.controls.forEach(
          (shipPackage, packageIndex: number) => {
            let packageControl = lastPackageArray?.at(packageIndex);

            if (!packageControl) {
              const packages = lastShipment.get(
                "freightEstimationSectionPackages"
              ) as FormArray;
              packages.push(this.createPackage());
              packageControl = lastPackageArray?.at(packageIndex);
            }

            packageControl?.patchValue({
              ...shipPackage.value,
            });
          }
        );
      }
    }
  }

  getAddressesFromShipments(shipmentReferenceId: string): FormGroup[] {
    const referencedShipments = this.shipments.controls.filter(
      (control: AbstractControl) => {
        const shipment = control as FormGroup;
        return shipment.value.shipmentReferenceId === shipmentReferenceId;
      }
    ) as FormGroup[];

    return referencedShipments;
  }

  appendPackage(shipmentIndex: number) {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const shipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const packages = shipment.get(
      "freightEstimationSectionPackages"
    ) as FormArray;
    packages.push(this.createPackage());

    this.isMetricChanged(shipmentIndex);

    const referencedShipments = this.getAddressesFromShipments(
      shipment.value.shipmentReferenceId
    );
    if (referencedShipments.length > 1) {
      const refShipmentIndex = referencedShipments.length - 1;
      const refShipment = referencedShipments[refShipmentIndex];
      const refPackages = refShipment.get(
        "freightEstimationSectionPackages"
      ) as FormArray;
      refPackages.push(this.createPackage());
    }
  }

  onPackageUpdate(shipmentReferenceId: string) {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const referencedShipments = shipmentArray.controls.filter(
      control => control.value.shipmentReferenceId === shipmentReferenceId
    ) as FormGroup[];

    if (referencedShipments.length > 1) {
      const firstShipment = referencedShipments[0];
      const firstPackagesArray = this.getPackageArray(firstShipment);

      const lastShipmentIndex = referencedShipments.length - 1;
      const lastShipment = referencedShipments[lastShipmentIndex];

      const lastPackageArray = this.getPackageArray(lastShipment);
      if (firstPackagesArray?.length > 0) {
        lastPackageArray.clear();
        firstPackagesArray.controls.forEach((shipPackage, index: number) => {
          let packageControl = lastPackageArray?.at(index);

          if (!packageControl) {
            const packages = lastShipment.get(
              "freightEstimationSectionPackages"
            ) as FormArray;
            packages.push(this.createPackage());

            this.isMetricChanged(lastShipmentIndex);
            packageControl = lastPackageArray?.at(index);
          }

          packageControl?.patchValue({
            ...shipPackage.value,
            id: packageControl.value.id,
            freightSectionId: lastShipment.value.id,
          });
        });
      }
    }
  }

  onWeightUpdate(packageRefId: string) {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    shipmentArray.controls.forEach(shipment => {
      const packages = (shipment as FormGroup).get(
        "freightEstimationSectionPackages"
      ) as FormArray;
      const matchingPackage = packages.controls.find(control => {
        return (
          (control as FormGroup).get("packageReferenceId")?.value ===
          packageRefId
        );
      });
      if (matchingPackage) {
        matchingPackage.patchValue({
          totalWeight:
            matchingPackage.value.packageCount *
            matchingPackage.value.packageWeight,
        });
      }
    });
  }

  removePackage(shipmentIndex: number, packageIndex: number) {
    const shipmentArray = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    const shipment = shipmentArray.at(shipmentIndex) as FormGroup;
    const packages = shipment.get(
      "freightEstimationSectionPackages"
    ) as FormArray;
    packages.removeAt(packageIndex);
  }

  getAddressByType(type: string): FormGroup | null {
    const addresses = this.freightForm
      .get("freightEstimationSections")
      ?.get("addresses") as FormArray;
    const address = addresses.controls.find(
      control => control.get("type")?.value === type
    );
    return address ? (address as FormGroup) : null;
  }

  clearAddress(shipment: FormGroup) {
    this.selectedAddressClear = shipment.get("shipmentReferenceId")?.value;
    this.clearAlertVisible = true;
  }

  confirmClearAddress(event: boolean) {
    if (event) {
      const shipmentArray = this.freightForm.get(
        "freightEstimationSections"
      ) as FormArray;
      const matchingShipments = shipmentArray.controls.filter(control => {
        return (
          (control as FormGroup).get("shipmentReferenceId")?.value ===
          this.selectedAddressClear
        );
      });
      matchingShipments.forEach(shipment => {
        const addresses = (shipment as FormGroup).get("addresses") as FormArray;
        if (addresses && addresses.length > 0) {
          addresses.controls.forEach(address => {
            if (address instanceof FormGroup) {
              Object.keys(address.controls).forEach(key => {
                const control = address.get(key);
                if (
                  control !== null &&
                  key !== "type" &&
                  key !== "freightSectionId"
                ) {
                  if (key === "id") {
                    control.patchValue(0);
                  } else if (key === "country") {
                    control.patchValue("USA");
                  } else {
                    control.patchValue("");
                  }
                }
              });
            }
          });
        }
      });
    }
    this.clearAlertVisible = false;
  }

  get groupShipments(): FormGroup[] {
    const uniqueShipmentMap = new Map<string, FormGroup>();

    this.shipments.controls.forEach((control: AbstractControl) => {
      const shipment = control as FormGroup; // Explicitly cast to FormGroup
      const shipmentRefId = shipment.value.shipmentReferenceId;

      if (!uniqueShipmentMap.has(shipmentRefId)) {
        uniqueShipmentMap.set(shipmentRefId, shipment);
      }
    });

    return Array.from(uniqueShipmentMap.values());
  }

  ngOnInit(): void {
    this.getCountryRegionListData();
    this.getAllCarriersList();
    this.freightForm.patchValue({
      name: this.jobName,
      salesOrderIdD365: this.saleOrderId,
    });

    if (this.quoteId && this.quoteId !== undefined) {
      this.getFreightEstimations(this.quoteId);
    }

    if (this.saleOrderId) {
      if (this.purchaseSaleOrderId) {
        this.getFreightEstimationShipments(
          "ponumber",
          this.purchaseSaleOrderId
        );
      } else {
        if (this.selectedLineItems.length > 0) {
          const salesOrders = this.selectedLineItems
            .map(item => item?.salesOrderLine?.lineNumberD365)
            .join(",");
          this.getFreightEstimationShipments(
            "SaleOrderLines",
            this.saleOrderId,
            salesOrders
          );
        } else {
          this.getFreightEstimationShipments(
            "salesordernumer",
            this.saleOrderId
          );
        }
      }
    }

    // if (this.selectedLineItems.length > 0 && this.selectedLineItems[0]?.address) {
    //   const lineAddressFrom = this.selectedLineItems[0]?.address;
    //   const address = this.getAddressArray(this.getShipment(0));
    //   const addressFrom = address.at(0);
    //   addressFrom.patchValue({
    //     Name: lineAddressFrom.addressD365,
    //     Country: lineAddressFrom.countryRegionIdD365,
    //     IsResidential: false,
    //     City: lineAddressFrom.cityD365,
    //     StateProvince: lineAddressFrom.stateD365,
    //   });
    // }

    // if (this.selectedLineItems.length > 1 && this.selectedLineItems[1]?.address) {
    //   const lineAddressTo = this.selectedLineItems[1]?.address;
    //   const address = this.getAddressArray(this.getShipment(0));
    //   const addressTo = address.at(1);

    //   addressTo.patchValue({
    //     Name: lineAddressTo.addressD365,
    //     Country: lineAddressTo.countryRegionIdD365,
    //     IsResidential: false,
    //     City: lineAddressTo.cityD365,
    //     StateProvince: lineAddressTo.stateD365,
    //   });
    // }

    this.formSubscription = this.freightForm.statusChanges.subscribe(status => {
      this.displayPrice = status === "VALID";
    });

    setTimeout(() => {
      this.freightFormValues = JSON.stringify(
        this.freightForm.value
      ) as unknown as FreightShipmentDto;
    }, 1000);
    this.freightForm.valueChanges.subscribe(value => {
      this.detectChanges(value);
    });
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  detectChanges(newValue: FreightShipmentDto) {
    const isChanged =
      (JSON.stringify(newValue) as unknown as FreightShipmentDto) !==
      this.freightFormValues;
    this.isFreightFormChanged = isChanged;
    this.formChanged.emit(this.isFreightFormChanged);
  }

  patchArrayData(freightSections: FreightShipmentSectionDto[]) {
    freightSections.forEach(
      (shipment: FreightShipmentSectionDto, shipmentIndex: number) => {
        this.onChangeCarrier(shipment.shippingCarrier, shipmentIndex);

        if (shipment.shipDate) {
          shipment.shipDate = new Date(shipment.shipDate);
        }

        let shipmentControl = this.getShipment(shipmentIndex);
        if (!shipmentControl) {
          this.appendShipment();
          shipmentControl = this.getShipment(shipmentIndex);
        }

        this.onChangeCarrier(shipment.shippingCarrier, shipmentIndex);
        shipmentControl?.patchValue({ ...shipment });

        const addressArray = shipmentControl?.get("addresses") as FormArray;
        if (shipment.addresses?.length > 0) {
          shipment.addresses.forEach(
            (
              address: FreightShipmentSectionAddressDto,
              addressIndex: number
            ) => {
              let addressControl = addressArray?.at(addressIndex);

              if (!addressControl) {
                this.appendNext(shipmentIndex);
                addressControl = addressArray?.at(addressIndex);
              }

              if (addressControl && !address.formattedAddress) {
                addressControl.patchValue({ formattedAddress: address.name });
              }
            }
          );
        }

        const packageArray = shipmentControl?.get(
          "freightEstimationSectionPackages"
        ) as FormArray;
        if (shipment.freightEstimationSectionPackages?.length > 0) {
          shipment.freightEstimationSectionPackages.forEach(
            (
              shipPackage: FreightShipmentSectionPackageDto,
              packageIndex: number
            ) => {
              let packageControl = packageArray?.at(packageIndex);

              if (!packageControl) {
                packageArray.push(this.createPackage());
                packageControl = packageArray?.at(packageIndex);
              }

              packageControl?.patchValue(shipPackage);
            }
          );
        }
      }
    );

    setTimeout(() => {
      this.freightForm.updateValueAndValidity();
    });
  }

  // transformKeysToCapitalized(obj: any): any {
  //   if (!obj || typeof obj !== 'object') return obj;

  //   if (Array.isArray(obj)) {
  //     return obj.map((item) => this.transformKeysToCapitalized(item));
  //   }

  //   return Object.keys(obj).reduce((acc, key) => {
  //     const newKey = key.charAt(0).toUpperCase() + key.slice(1);
  //     const value = obj[key];

  //     acc[newKey] = this.transformKeysToCapitalized(value);

  //     return acc;
  //   }, {} as any);
  // }

  getCountryRegionListData() {
    this._addNewClientService
      .getCountryRegionList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.countryRegionList = res?.data;
        },
      });
  }

  getAllCarriersList() {
    this._freightQuoteService
      .getFreightCarriersList()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            const groupedCarries = this.groupCarriers(res.data);
            this.carriers = groupedCarries;
            if (this.freightForm.value?.id === 0) {
              this.onChangeCarrier("FEDEX", 0);
            }
          } else {
            this._toastr.error("Error", "Failed to load");
          }
        },
        error: () => {
          this._toastr.error("Error", "Failed to load");
        },
      });
  }

  getFreightEstimationShipments(
    property: string,
    orderId: string,
    lineItems?: string
  ) {
    this._freightQuoteService
      .getFreightEstimationShipments(property, orderId, lineItems)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            if (
              res.data.freightEstimationSections &&
              res.data.freightEstimationSections.length > 0
            ) {
              this.patchFreightDetails(res.data);
            } else {
              this._toastr.error("Error", "No details found!");
            }
          } else {
            this._toastr.error("Error", "Failed to load");
          }
        },
        error: () => {
          this._toastr.error("Error", "Failed to load");
        },
      });
  }

  groupCarriers(data: FreightCarriersListDto[]): FreightCarriersGroupDto[] {
    const grouped = data.reduce((acc: FreightCarriersGroupDto[], item) => {
      const carrierIndex = acc.findIndex(
        group => group.carrier === item.carrier
      );
      if (carrierIndex === -1) {
        acc.push({
          carrier: item.carrier ?? "",
          data: [item],
        });
      } else {
        acc[carrierIndex].data.push(item);
      }
      return acc;
    }, []);
    return grouped;
  }

  setCarrierDescription(event: SelectChangeEvent, shipmentIndex: number) {
    const currentShipment = this.shipments.at(shipmentIndex) as FormGroup;
    const shipmentReferenceId = currentShipment.value.shipmentReferenceId;
    const target = event.originalEvent.target as HTMLSelectElement;
    const referencedShipments = this.shipments.controls.filter(
      (control: AbstractControl): control is FormGroup => {
        return (
          control instanceof FormGroup &&
          control.value.shipmentReferenceId === shipmentReferenceId
        );
      }
    );
    if (referencedShipments.length > 0) {
      referencedShipments.forEach((shipment: FormGroup) => {
        shipment.patchValue({
          shipmentMethodCode: event.value,
          shipmentMethodDescription: target?.innerText,
        });
      });
    }
  }

  onChangeCarrier(carrier: string, indexNumber?: number) {
    if (carrier) {
      let index = indexNumber;
      this.selectedCarrier = carrier;
      const findByCarrier = this.carriers.find(
        carrierItem => carrierItem.carrier === this.selectedCarrier
      );

      if (findByCarrier) {
        if (index !== undefined) {
          this.selectedCarrierList[index] = findByCarrier.data ?? [];
        } else {
          index = this.shipments.length - 1;
          this.selectedCarrierList[index] = findByCarrier.data ?? [];
        }

        const currentShipment = this.shipments.at(index) as FormGroup;
        if (currentShipment) {
          const shipmentReferenceId = currentShipment.value.shipmentReferenceId;
          const referencedShipments = this.shipments.controls.filter(
            (control: AbstractControl): control is FormGroup => {
              return (
                control instanceof FormGroup &&
                control.value.shipmentReferenceId === shipmentReferenceId
              );
            }
          );
          if (referencedShipments.length > 0) {
            referencedShipments.forEach((shipment: FormGroup) => {
              shipment.patchValue({
                shippingCarrier: carrier,
              });
            });
          }
        }
      }
    }
  }

  /* eslint-disable @typescript-eslint/no-unsafe-call */
  totalPackagesSum(packages: FormArray, shipment: FormGroup) {
    const totalSum = packages.value.reduce(
      (sum: number, obj: { packageCount: string | number }) =>
        sum + Number(obj.packageCount || 0),
      0
    ) as number;
    shipment.patchValue({
      TotalPackages: totalSum,
    });

    return totalSum;
  }

  totalWeightSum(packages: FormArray, shipment: FormGroup) {
    const totalSum = packages.value.reduce(
      (sum: number, obj: { totalWeight: string | number }) =>
        sum + Number(obj.totalWeight || 0),
      0
    ) as number;
    shipment.patchValue({
      TotalWeight: totalSum,
    });

    return totalSum;
  }
  /* eslint-enable @typescript-eslint/no-unsafe-call */

  saveQuotation() {
    this._loaderService.setLoaderState(true);
    if (this.freightForm.value?.id && this.freightForm.value?.id !== 0) {
      this.updateFreightEstimation();
    } else {
      this.addFreightEstimation();
    }
  }

  addFreightEstimation() {
    if (this.freightForm.valid) {
      this.fixShipDates();
      this._freightQuoteService
        .addFreightEstimation(
          this.freightForm.value as FreightShipmentRequestDto
        )
        .subscribe({
          next: res => {
            this.patchFreightDetails(res.data);
            this.displayPrice = true;
            this._loaderService.setLoaderState(false);
            this._toastr.success(
              "Success",
              "Shipment details has been updated!"
            );
            setTimeout(() => {
              this.freightFormValues = JSON.stringify(
                this.freightForm.value
              ) as unknown as FreightShipmentDto;
            }, 1000);
          },
          error: () => {
            this._loaderService.setLoaderState(false);
          },
        });
    } else {
      this.freightForm.markAllAsTouched();
      this._toastr.error("Error", "Fill required fields to proceed");
      this._loaderService.setLoaderState(false);
    }
  }

  updateFreightEstimation() {
    this.fixShipDates();
    this._freightQuoteService
      .updateFreightEstimation(
        this.freightForm.value as FreightShipmentRequestDto
      )
      .subscribe({
        next: () => {
          this._toastr.success("Success", "Shipment details has been updated!");
          this._loaderService.setLoaderState(false);
          this.displayPrice = true;
          setTimeout(() => {
            this.freightFormValues = JSON.stringify(
              this.freightForm.value
            ) as unknown as FreightShipmentDto;
          }, 1000);
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  fixShipDates = (): void => {
    this.shipments.controls.forEach((control: AbstractControl) => {
      const shipment = control as FormGroup;
      if (shipment.value?.shipDate) {
        // Get the date object and reset time to 00:00:00
        const originalDate = new Date(shipment.value.shipDate);
        // Create UTC date object with time set to 00:00:00
        const dateOnly = new Date(
          Date.UTC(
            originalDate.getFullYear(),
            originalDate.getMonth(),
            originalDate.getDate(),
            0, // hours = 0
            0, // minutes = 0
            0, // seconds = 0
            0 // milliseconds = 0
          )
        );

        shipment.patchValue({
          shipDate: dateOnly,
        });
      }
    });
  };

  getEstimations() {
    this._loaderService.setLoaderState(true);
    this.fixShipDates();
    this._freightQuoteService
      .getFreightQuote(this.freightForm.value as FreightShipmentRequestDto)
      .subscribe({
        next: res => {
          if (res?.data) {
            this.estimations = res.data;
            this.patchFreightDetails(res.data);
            if (res.data?.apiData) {
              const shipmentList = res.data?.apiData?.shipmentList;
              if (shipmentList) {
                const errorLines: string[] = [];
                shipmentList.forEach((shipment: ShipmentList, i: number) => {
                  if (shipment?.responseCode !== 0) {
                    const alerts = ([] as AlertList[]).concat(
                      shipment.shipmentAlertList ?? []
                    );
                    alerts.forEach((al, j) => {
                      const desc = al?.alertDescription ?? "Unknown error";
                      errorLines.push(`${i + 1}.${j + 1} ${desc}`);
                    });
                  }
                });
                if (errorLines.length) {
                  this._toastr.error(
                    errorLines.join("<br>"),
                    "Shipment error(s)",
                    true
                  );
                }
              }
            }
            this._loaderService.setLoaderState(false);
          }
        },
        error: () => {
          this._loaderService.setLoaderState(false);
        },
      });
  }

  getFreightEstimations(quoteId: number) {
    this._loaderService.setLoaderState(true);

    this._freightQuoteService
      .getFreightEstimation(quoteId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res?.data) {
            this.estimations = res.data;
            this.patchFreightDetails(res.data);
            this._loaderService.setLoaderState(false);
          } else {
            this._toastr.error("Error", "Failed to load");
            this._loaderService.setLoaderState(false);
          }
        },
        error: () => {
          this._toastr.error("Error", "Failed to load");
          this._loaderService.setLoaderState(false);
        },
      });
  }

  patchFreightDetails(details: FreightShipmentDto) {
    this.freightForm.patchValue(details);
    this.patchArrayData(details.freightEstimationSections);
    setTimeout(() => {
      this.freightFormValues = JSON.stringify(
        this.freightForm.value
      ) as unknown as FreightShipmentDto;
    }, 1000);
  }

  isMetricChanged(index: number) {
    const currentShipment = this.shipments.at(index) as FormGroup;
    if (currentShipment) {
      const shipmentReferenceId = currentShipment.value.shipmentReferenceId;
      const referencedShipments = this.shipments.controls.filter(
        (control: AbstractControl) => {
          return (
            (control as FormGroup).value.shipmentReferenceId ===
            shipmentReferenceId
          );
        }
      ) as FormGroup[];
      if (referencedShipments.length > 0) {
        const metrics = {
          weightUnitCode: currentShipment.value.isMetricUnits ? "KG" : "LB",
          dimentsionUnitCode: currentShipment.value.isMetricUnits ? "CM" : "IN",
        };
        referencedShipments.forEach((shipment: FormGroup) => {
          shipment.patchValue({
            isMetricUnits: currentShipment.value.isMetricUnits,
          });
          const packages = this.getPackageArray(shipment);
          packages.controls.forEach((pkg: AbstractControl) => {
            pkg.patchValue(metrics);
          });
        });
      }
    }
  }

  onSelectMultiPricing(event: FreightShipmentDto) {
    this.shippingResponse = event;
  }

  getQouteTotalPrice(price: string) {
    this.qouteTotalPrice = price;
  }

  shipDateChange(index: number) {
    const currentShipment = this.shipments.at(index) as FormGroup;
    if (currentShipment) {
      const shipmentReferenceId = currentShipment.value.shipmentReferenceId;
      const shipmentDate = currentShipment.value.shipDate;
      const referencedShipments = this.shipments.controls.filter(
        (control: AbstractControl): control is FormGroup => {
          return (
            control instanceof FormGroup &&
            control.value.shipmentReferenceId === shipmentReferenceId
          );
        }
      );
      if (referencedShipments.length > 0) {
        referencedShipments.forEach((shipment: FormGroup) => {
          shipment.patchValue({
            shipDate: shipmentDate,
          });
        });
      }
    }
  }

  addressResChange(event: ResidentialEvent, shipment: FormGroup) {
    const addresses = this.getAddressArray(shipment);
    const address = addresses.controls.find(
      control => control.get("type")?.value === event.type
    );
    if (address) {
      address.patchValue({
        isResidential: event.check,
      });
    }
  }

  metricChange(event: boolean, shipIndex: number) {
    const currentShipment = this.shipments.at(shipIndex) as FormGroup;
    if (currentShipment) {
      const flag = event ? true : currentShipment.value.isMetricUnits;
      currentShipment.patchValue({
        isMetricUnits: flag,
      });
      this.isMetricChanged(shipIndex);
    }
  }

  onCloseModalEvent() {
    this.closeModalEvent.emit();
  }

  removeShipment(shipIndex: number) {
    const shipments = this.freightForm.get(
      "freightEstimationSections"
    ) as FormArray;
    shipments.removeAt(shipIndex);
  }
}
